import React, { useState } from 'react';
import { toast } from 'sonner';
import { Wallet, Upload, Eye, X } from 'lucide-react';
import { inr, ADMINS } from './helpers';
import { uploadFile, srcForPath } from '@/lib/uploads';

// ===========================================================================
// PURE BUILDERS — preserve exact field shape expected by FinanceTab/helpers.
// DO NOT change the names or null-emptying behaviour without explicit instruction.
// ===========================================================================

const EMPTY_ADV = {
  advance: 0, advance_mode: null, advance_paid_to: null, advance_upi_paid_to: null,
  advance_admin_name: null, advance_confirmed: false, advance_shot: null,
  advance_collection_date: null, advance_handover_date: null,
};

function buildAdvanceFields({ mode, paidTo, adminName, shot, amount, work }) {
  if (!paidTo) throw new Error(`Choose who received the ${mode} advance (Staff or Admin)`);
  const out = {
    advance: amount,
    advance_mode: mode,
    advance_paid_to: paidTo,
    // Keep the legacy advance_upi_paid_to for old finance code paths, only when UPI
    advance_upi_paid_to: mode === 'UPI' ? paidTo : null,
    advance_admin_name: null,
    advance_shot: null,
    advance_collection_date: work.advance_collection_date || new Date().toISOString(),
  };
  if (paidTo === 'Admin') {
    if (!adminName) throw new Error('Select which admin received the advance');
    out.advance_admin_name = adminName;
  }
  // UPI to admin needs a screenshot proof
  if (mode === 'UPI' && paidTo === 'Admin') out.advance_shot = shot;
  return out;
}

function buildPayFields({ payMode, cashPaidTo, upiPaidTo, adminName, upiShot }) {
  if (payMode === 'Pending' || payMode === 'Bank Credit') {
    return { pay_mode: payMode, upi_shot: null, pay_upi_paid_to: null, pay_cash_paid_to: null, pay_admin_name: null };
  }
  if (payMode === 'Cash') {
    if (!cashPaidTo) throw new Error('Choose who received the Cash balance (Staff or Admin)');
    const out = { pay_mode: payMode, pay_cash_paid_to: cashPaidTo, upi_shot: null, pay_upi_paid_to: null, pay_admin_name: null };
    if (cashPaidTo === 'Admin') {
      if (!adminName) throw new Error('Select which admin received the Cash balance');
      out.pay_admin_name = adminName;
    }
    return out;
  }
  // UPI
  if (!upiPaidTo) throw new Error('Choose who received the UPI balance (Staff or Admin)');
  const out = { pay_mode: payMode, pay_upi_paid_to: upiPaidTo, pay_cash_paid_to: null, pay_admin_name: null, upi_shot: null };
  if (upiPaidTo === 'Admin') {
    if (!adminName) throw new Error('Select which admin received the UPI balance');
    out.pay_admin_name = adminName;
    out.upi_shot = upiShot;
  }
  return out;
}

// ===========================================================================
// SHARED Paid-To picker (Cash + UPI both reuse this)
// ===========================================================================
function PaidToRouting({ label, paidTo, setPaidTo, adminName, setAdminName, shot, setShot, showShot, busy, setBusy, onView, testid }) {
  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error('Image too large (max 6MB)'); return; }
    setBusy(true);
    try {
      const path = await uploadFile('upi-screenshots', f);
      setShot(path);
      toast.success('Screenshot uploaded');
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 6 }}>
      <label className="field-label">{label} — Paid To</label>
      <div className="seg" data-testid={`${testid}-paidto`}>
        {['Staff', 'Admin'].map((p) => (
          <button key={p} type="button" className={paidTo === p ? 'on' : ''} onClick={() => setPaidTo(p)}>{p}</button>
        ))}
      </div>
      {paidTo === 'Admin' && (
        <AdminWithShot adminName={adminName} setAdminName={setAdminName}
          shot={shot} setShot={setShot} showShot={showShot} busy={busy}
          upload={upload} onView={onView} testid={testid} />
      )}
    </div>
  );
}

function AdminWithShot({ adminName, setAdminName, shot, setShot, showShot, busy, upload, onView, testid }) {
  return (
    <div style={{ marginTop: 6 }}>
      <label className="field-label">Which Admin</label>
      <select className="field-input" value={adminName}
        onChange={(e) => setAdminName(e.target.value)} data-testid={`${testid}-admin`}>
        <option value="">— Select admin —</option>
        {ADMINS.map((a) => <option key={a.user_id} value={a.name}>{a.name}</option>)}
      </select>
      {showShot && <ShotRow shot={shot} setShot={setShot} busy={busy} upload={upload} onView={onView} testid={testid} />}
    </div>
  );
}

function ShotRow({ shot, setShot, busy, upload, onView, testid }) {
  if (shot) {
    return (
      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        <img className="shot-thumb" src={srcForPath(shot)} alt="proof" data-testid={`${testid}-thumb`} />
        <button type="button" className="confirm-badge confirmed-yes" onClick={() => onView && onView(shot)}><Eye className="icon" /> View</button>
        <button type="button" className="btn-outline" style={{ flex: 'none', padding: '6px 8px' }} onClick={() => setShot(null)}><X className="icon" /></button>
      </div>
    );
  }
  return (
    <label className="confirm-badge" style={{ marginTop: 6, background: '#f5f3ff', color: '#6d28d9', borderColor: '#c4b5fd', cursor: 'pointer' }} data-testid={`${testid}-upload`}>
      <Upload className="icon" /> {busy ? 'Adding…' : 'Upload Screenshot'}
      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={upload} />
    </label>
  );
}

// ===========================================================================
// Sub-section: Advance card (top yellow card)
// ===========================================================================
function AdvanceCard(props) {
  const { advance, setAdvance, advanceMode, setAdvanceMode, adv,
    advPaidTo, setAdvPaidTo, advAdminName, setAdvAdminName,
    advShot, setAdvShot, advBusy, setAdvBusy, onViewShot } = props;
  return (
    <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 10, padding: 12, marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#92400e', letterSpacing: '0.5px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        💰 ADVANCE FROM CLIENT (OPTIONAL)
      </div>
      <div className="field">
        <label className="field-label">Advance Amount ₹</label>
        <input className="field-input" type="number" inputMode="numeric" value={advance}
          onChange={(e) => setAdvance(e.target.value)}
          style={{ fontSize: 16, fontWeight: 700 }} data-testid="advance-input" />
      </div>
      {adv > 0 && (
        <>
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="field-label">Mode</label>
            <div className="seg" data-testid="advance-mode">
              {['Cash', 'UPI'].map((m) => (
                <button key={m} type="button" className={advanceMode === m ? 'on' : ''} onClick={() => setAdvanceMode(m)}>{m}</button>
              ))}
            </div>
          </div>
          <PaidToRouting label="Advance"
            paidTo={advPaidTo} setPaidTo={setAdvPaidTo}
            adminName={advAdminName} setAdminName={setAdvAdminName}
            shot={advShot} setShot={setAdvShot}
            showShot={advanceMode === 'UPI'}
            busy={advBusy} setBusy={setAdvBusy} onView={onViewShot} testid="adv" />
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Sub-section: Balance Payment block (Pending/Cash/UPI/Bank Credit)
// ===========================================================================
function BalanceBlock(props) {
  const { payMode, setPayMode,
    payCashPaidTo, setPayCashPaidTo,
    payUpiPaidTo, setPayUpiPaidTo,
    payAdminName, setPayAdminName,
    upiShot, setUpiShot, payBusy, setPayBusy, onViewShot } = props;
  return (
    <div className="field">
      <label className="field-label">Balance Payment Mode</label>
      <select className="field-input" value={payMode} onChange={(e) => setPayMode(e.target.value)} data-testid="paymode-select">
        {['Pending', 'Cash', 'UPI', 'Bank Credit'].map((m) => <option key={m}>{m}</option>)}
      </select>
      {payMode === 'Cash' && (
        <PaidToRouting label="Balance"
          paidTo={payCashPaidTo} setPaidTo={setPayCashPaidTo}
          adminName={payAdminName} setAdminName={setPayAdminName}
          shot={null} setShot={() => {}} showShot={false}
          busy={false} setBusy={() => {}} onView={onViewShot} testid="paycash" />
      )}
      {payMode === 'UPI' && (
        <PaidToRouting label="Balance"
          paidTo={payUpiPaidTo} setPaidTo={setPayUpiPaidTo}
          adminName={payAdminName} setAdminName={setPayAdminName}
          shot={upiShot} setShot={setUpiShot} showShot
          busy={payBusy} setBusy={setPayBusy} onView={onViewShot} testid="payupi" />
      )}
      {payMode === 'Bank Credit' && (
        <div style={{ fontSize: 10, color: '#78716c', fontStyle: 'italic', padding: '6px 2px' }}>
          Mark as paid in the Bank Fee / Finance tab once the bank pays the fee.
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Main modal
// ===========================================================================
export default function FeeModal({ work, staff = [], onClose, onSave, onViewShot }) {
  const [originalFee, setOriginalFee] = useState(work.fees || '');
  const [discount, setDiscount] = useState(work.discount || '');
  const [advance, setAdvance] = useState(work.advance || '');
  const [advanceMode, setAdvanceMode] = useState(work.advance_mode || 'Cash');
  // Advance routing (Cash OR UPI both use Staff/Admin picker)
  const [advPaidTo, setAdvPaidTo] = useState(work.advance_paid_to || work.advance_upi_paid_to || '');
  const [advAdminName, setAdvAdminName] = useState(work.advance_admin_name || (work.advance_paid_to === 'Admin' ? work.admin_name : '') || '');
  const [advShot, setAdvShot] = useState(work.advance_shot || null);
  // Balance routing
  const [payMode, setPayMode] = useState(work.pay_mode || 'Pending');
  const [payCashPaidTo, setPayCashPaidTo] = useState(work.pay_cash_paid_to || '');
  const [payUpiPaidTo, setPayUpiPaidTo] = useState(work.pay_upi_paid_to || '');
  const [payAdminName, setPayAdminName] = useState(work.pay_admin_name || work.admin_name || '');
  const [upiShot, setUpiShot] = useState(work.upi_shot || null);
  const [advBusy, setAdvBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const orig = Number(originalFee) || 0;
  const disc = Number(discount) || 0;
  const adv = Number(advance) || 0;
  const total = Math.max(0, orig - disc);
  const balance = Math.max(0, total - adv);

  // Validate and assemble the final patch.
  const build = () => {
    const oV = String(originalFee).trim();
    const aV = String(advance).trim();

    // Both blank → fully clear fee + advance.
    if (oV === '' && aV === '') return { fees: 0, discount: 0, ...EMPTY_ADV };

    // Advance set without original fee (admin will set fee later).
    if (oV === '') {
      const n = Number(aV) || 0;
      const advPart = n > 0
        ? buildAdvanceFields({ mode: advanceMode, paidTo: advPaidTo, adminName: advAdminName, shot: advShot, amount: n, work })
        : EMPTY_ADV;
      const payPart = buildPayFields({ payMode, cashPaidTo: payCashPaidTo, upiPaidTo: payUpiPaidTo, adminName: payAdminName, upiShot });
      return { fees: 0, discount: 0, ...advPart, ...payPart };
    }

    // Normal path: original fee provided.
    if (disc > orig) throw new Error('Discount cannot exceed the original fee');
    if (adv > total) throw new Error('Advance cannot exceed the net amount');
    const advPart = adv > 0
      ? buildAdvanceFields({ mode: advanceMode, paidTo: advPaidTo, adminName: advAdminName, shot: advShot, amount: adv, work })
      : EMPTY_ADV;
    const payPart = buildPayFields({ payMode, cashPaidTo: payCashPaidTo, upiPaidTo: payUpiPaidTo, adminName: payAdminName, upiShot });
    return { fees: orig, discount: disc, ...advPart, ...payPart };
  };

  const save = async () => {
    let patch;
    try { patch = build(); } catch (e) { toast.error(e.message); return; }
    setSaving(true);
    try { await onSave(work, patch); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 440 }} data-testid="fee-modal">
        <div className="modal-header">
          <h3 style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Wallet style={{ width: 16, height: 16 }} /> Set Fee &amp; Advance
          </h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <AdvanceCard
          advance={advance} setAdvance={setAdvance}
          advanceMode={advanceMode} setAdvanceMode={setAdvanceMode}
          adv={adv}
          advPaidTo={advPaidTo} setAdvPaidTo={setAdvPaidTo}
          advAdminName={advAdminName} setAdvAdminName={setAdvAdminName}
          advShot={advShot} setAdvShot={setAdvShot}
          advBusy={advBusy} setAdvBusy={setAdvBusy}
          onViewShot={onViewShot} />

        <div className="field">
          <label className="field-label">Original Fee ₹ <span style={{ color: '#a8a29e', fontWeight: 500, fontStyle: 'italic' }}>(can be set later by admin)</span></label>
          <input className="field-input" type="number" inputMode="numeric" value={originalFee}
            onChange={(e) => setOriginalFee(e.target.value)} style={{ fontSize: 18, fontWeight: 700 }} data-testid="fee-input" />
        </div>
        <div className="field">
          <label className="field-label">🏷 Discount ₹ (optional)</label>
          <input className="field-input" type="number" inputMode="numeric" value={discount}
            onChange={(e) => setDiscount(e.target.value)} data-testid="discount-input" />
        </div>

        <BalanceBlock
          payMode={payMode} setPayMode={setPayMode}
          payCashPaidTo={payCashPaidTo} setPayCashPaidTo={setPayCashPaidTo}
          payUpiPaidTo={payUpiPaidTo} setPayUpiPaidTo={setPayUpiPaidTo}
          payAdminName={payAdminName} setPayAdminName={setPayAdminName}
          upiShot={upiShot} setUpiShot={setUpiShot}
          payBusy={payBusy} setPayBusy={setPayBusy}
          onViewShot={onViewShot} />

        {orig > 0 && (
          <div className="calc-box box-summary" data-testid="summary-box">
            <div className="calc-row"><span>Total Amount:</span><span className="mono" style={{ fontWeight: 700 }}>{inr(total)}</span></div>
            <div className="calc-row" style={{ color: '#047857' }}><span>Advance Paid:</span><span className="mono" style={{ fontWeight: 700 }}>{inr(adv)}</span></div>
            <div className="calc-row grand"><span>Balance Due from Client:</span><span className="mono">{inr(balance)}</span></div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving} data-testid="save-fee-btn">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
